import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StatusPill, CountdownChip, fmtDate } from "./ReturnsShared";
import AdminRefundControl from "@/components/AdminRefundControl";

export default function AdminReturnsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [ticking, setTicking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/returns");
      setRows(data || []);
    } catch { toast.error("Could not load return requests"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const runTick = async () => {
    setTicking(true);
    try {
      const { data } = await api.post("/internal/returns/tick");
      toast.success(`SLA sweep · reminded ${data.reminded}, escalated ${data.escalated}`);
      load();
    } catch { toast.error("Could not run SLA sweep"); }
    finally { setTicking(false); }
  };

  const counts = useMemo(() => ({
    all: rows.length,
    pending_vendor: rows.filter((r) => r.status === "pending_vendor").length,
    accepted: rows.filter((r) => r.status === "accepted").length,
    escalated: rows.filter((r) => (r.status || "").startsWith("escalated_")).length,
    completed: rows.filter((r) => r.status === "completed").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  }), [rows]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "escalated") return rows.filter((r) => (r.status || "").startsWith("escalated_"));
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const chips = [
    ["all", "All"],
    ["pending_vendor", "Awaiting vendor"],
    ["accepted", "In fulfilment"],
    ["escalated", "Escalated"],
    ["completed", "Completed"],
    ["rejected", "Rejected"],
  ];

  return (
    <div className="space-y-4" data-testid="admin-returns-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl">Returns & Replacements</div>
          <div className="text-xs text-gray-500">Monitor SLA countdowns and step in when escalations open.</div>
        </div>
        <Button data-testid="admin-returns-tick" onClick={runTick} disabled={ticking} variant="outline" className="rounded-full">
          {ticking ? "Running…" : "Run SLA sweep now"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {chips.map(([k, label]) => (
          <button
            key={k}
            data-testid={`admin-returns-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`p-3 rounded-2xl border text-left ${filter === k ? "border-[#F48E2E] bg-[#FEF3E6]" : "border-gray-200 bg-white"}`}
          >
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
            <div className="font-display text-2xl">{counts[k] ?? 0}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-10 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-500 py-16 text-center border border-dashed border-gray-200 rounded-2xl">No requests in this bucket.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Request</th>
                <th className="text-left px-4 py-3">Buyer / Exhibitor</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">SLA</th>
                <th className="text-left px-4 py-3">Refund</th>
                <th className="text-left px-4 py-3">Raised</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`admin-return-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {r.product_image && <img src={r.product_image} alt="" className="h-10 w-10 rounded-md object-cover" />}
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[220px]">{r.product_name}</div>
                        <div className="text-[11px] text-gray-500">Order #{(r.order_id || "").slice(0, 8).toUpperCase()}</div>
                        <div className="text-xs text-gray-600 line-clamp-1 max-w-[260px]">{r.reason}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    <div>{r.user_email || "buyer"}</div>
                    <div className="text-gray-400">{r.vendor_business_name || "Exhibitor"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs capitalize">{r.kind}</td>
                  <td className="px-4 py-3"><StatusPill status={r.status} testid={`admin-return-status-${r.id}`} /></td>
                  <td className="px-4 py-3">
                    {r.status === "pending_vendor" && <CountdownChip deadlineIso={r.vendor_deadline} testid={`admin-return-clock-${r.id}`} prefix="48h" />}
                    {r.status === "accepted" && <CountdownChip deadlineIso={r.fulfil_deadline} testid={`admin-fulfil-clock-${r.id}`} prefix="5d" />}
                    {(r.status || "").startsWith("escalated_") && <span className="text-[11px] text-red-600">{r.escalation_reason}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.refund_status && r.refund_status !== "not_required"
                      ? <AdminRefundControl kind="return" target={r} compact onDone={load} />
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
